import { Directive, ElementRef, HostBinding, HostListener } from '@angular/core';

/**
 * Keyboard support for a hand-made tab list (WAI-ARIA tabs pattern, DGA criterion 25).
 * Put it on the element with role="tablist"; the tabs are its [role="tab"] descendants, each with
 * aria-selected and a roving tabindex (0 on the selected tab, -1 on the others) bound in the template.
 * Arrow keys (mirrored in right-to-left pages; Up/Down too) and Home/End move focus and activate the tab
 * by clicking it, so the existing (click) handler stays the only place that selects a tab.
 * Enter and Space activate tabs that are not native buttons.
 */
@Directive({
  selector: '[ucTablist]',
})
export class TablistDirective {
  @HostBinding('attr.role') readonly role = 'tablist';

  constructor(private host: ElementRef<HTMLElement>) { }

  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    const tabs = this.tabs();
    const current = tabs.indexOf((event.target as HTMLElement)?.closest('[role="tab"]') as HTMLElement);
    if (current < 0) return;

    const target = this.targetIndex(event.key, current, tabs.length);
    if (target === null) return;

    event.preventDefault();
    tabs[target].focus();
    tabs[target].click();
  }

  private targetIndex(key: string, current: number, count: number): number | null {
    const rtl = getComputedStyle(this.host.nativeElement).direction === 'rtl';
    const next = (current + 1) % count;
    const previous = (current - 1 + count) % count;
    switch (key) {
      case 'ArrowRight': return rtl ? previous : next;
      case 'ArrowLeft': return rtl ? next : previous;
      case 'ArrowDown': return next;
      case 'ArrowUp': return previous;
      case 'Home': return 0;
      case 'End': return count - 1;
      case 'Enter':
      case ' ':
        return this.tabs()[current].tagName === 'BUTTON' ? null : current;
      default: return null;
    }
  }

  private tabs(): HTMLElement[] {
    return Array.from(this.host.nativeElement.querySelectorAll<HTMLElement>('[role="tab"]'))
      .filter(tab => !tab.hasAttribute('disabled') && tab.getAttribute('aria-disabled') !== 'true');
  }
}
